/**
 * Italian render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'it')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const italianLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'sfuocare' },
    change: { primary: 'cambio' },
    click: { primary: 'clic' },
    dblclick: { primary: 'doppioclic' },
    focus: { primary: 'fuoco' },
    input: { primary: 'input' },
    keydown: { primary: 'tastogiù' },
    keypress: { primary: 'tastopremi' },
    keyup: { primary: 'tastosu' },
    load: { primary: 'carica' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'mouseentra' },
    mouseleave: { primary: 'mouseesce' },
    mousemove: { primary: 'mousemuovi' },
    mouseout: { primary: 'mousefuori' },
    mouseover: { primary: 'mousesopra' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'reimpostare' },
    resize: { primary: 'ridimensiona' },
    scroll: { primary: 'scorrimento' },
    submit: { primary: 'invio' },
    touchcancel: { primary: 'toccoannulla' },
    touchend: { primary: 'toccofine' },
    touchmove: { primary: 'toccomuovi' },
    touchstart: { primary: 'toccoinizia' },
    unload: { primary: 'scarica' },
  },
  logical: {
    and: { primary: 'e' },
    changes: { primary: 'cambia' },
    contains: { primary: 'contiene' },
    else: { primary: 'altrimenti' },
    end: { primary: 'fine' },
    equals: { primary: 'uguale' },
    exists: { primary: 'esiste' },
    has: { primary: 'ha' },
    have: { primary: 'ho' },
    includes: { primary: 'include' },
    is: { primary: 'è' },
    live: { primary: 'vivo' },
    matches: { primary: 'corrisponde' },
    not: { primary: 'non' },
    or: { primary: 'o' },
    otherwise: { primary: 'altrimenti' },
    then: { primary: 'allora' },
    when: { primary: 'quando' },
    where: { primary: 'dove' },
  },
  temporal: {
    h: { primary: 'h' },
    hour: { primary: 'ora' },
    hours: { primary: 'ore' },
    millisecond: { primary: 'millisecondo' },
    milliseconds: { primary: 'millisecondi' },
    min: { primary: 'min' },
    minute: { primary: 'minuto' },
    minutes: { primary: 'minuti' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'secondo' },
    seconds: { primary: 'secondi' },
    times: { primary: 'volte' },
  },
  values: {
    body: { primary: 'corpo' },
    detail: { primary: 'dettaglio' },
    document: { primary: 'documento' },
    element: { primary: 'elemento' },
    event: { primary: 'evento' },
    false: { primary: 'falso' },
    it: { primary: 'esso' },
    its: { primary: 'suo' },
    me: { primary: 'io' },
    my: { primary: 'mio' },
    myself: { primary: 'me stesso' },
    null: { primary: 'nullo' },
    result: { primary: 'risultato' },
    target: { primary: 'obiettivo' },
    true: { primary: 'vero' },
    undefined: { primary: 'indefinito' },
    value: { primary: 'valore' },
    window: { primary: 'finestra' },
    you: { primary: 'tu' },
    your: { primary: 'tuo' },
    yourself: { primary: 'te stesso' },
  },
  attributes: {
    attribute: { primary: 'attributo' },
    attributes: { primary: 'attributi' },
    class: { primary: 'classe' },
    classes: { primary: 'classi' },
    properties: { primary: 'proprietà' },
    property: { primary: 'proprietà' },
    style: { primary: 'stile' },
    styles: { primary: 'stili' },
  },
  expressions: {
    at: { primary: 'a' },
    characters: { primary: 'caratteri' },
    children: { primary: 'figli' },
    closest: { primary: 'vicino' },
    empty: { primary: 'vuoto' },
    'ends with': { primary: 'finisce con' },
    exclusive: { primary: 'esclusivo' },
    first: { primary: 'primo' },
    'ignoring case': { primary: 'ignorando maiuscole' },
    inclusive: { primary: 'inclusivo' },
    'joined by': { primary: 'unito per' },
    last: { primary: 'ultimo' },
    'mapped to': { primary: 'trasformato in' },
    next: { primary: 'prossimo' },
    no: { primary: 'nessun' },
    parent: { primary: 'genitore' },
    prev: { primary: 'prec' },
    previous: { primary: 'precedente' },
    random: { primary: 'casuale' },
    some: { primary: 'qualche' },
    'sorted by': { primary: 'ordinato per' },
    'split by': { primary: 'diviso per' },
    'starts with': { primary: 'inizia con' },
    within: { primary: 'dentro' },
  },
};

registerLexicon('it', italianLexicon);
